import React from 'react'
import { Tooltip } from '@mui/material'
import TableCell from '../TableCell'
import ActionsMenu from './ActionsMenu'
import StatusBadge from './StatusBadge'
import VoucherBadge, { TOOLTIP_TOUCH_LINGER_MS } from './VoucherBadge'
import NumberChip from './NumberChip'
import { OrdersTab } from './columns'
import { formatDurationHms } from '../../../utils/formatDurationHms'
import { t } from '../../../i18n'
import { OrderRow, ActionItem, EMPTY_PLACEHOLDER } from '../types'

const scope = 'orders'

const openLockerItem = (queuedOrder: OrderRow): ActionItem | undefined => {
  if (!queuedOrder.locker_openable) return

  return {
    key: 'open_locker',
    href: `queued_orders/${queuedOrder.id}/open_locker`,
    label: t('open_locker', { scope, number: queuedOrder.locker_number }),
    method: 'put'
  }
}

const prioritizeItem = (queuedOrder: OrderRow): ActionItem | undefined => {
  if (queuedOrder.record_type !== 'queued_order') return
  if (queuedOrder.type !== 'meal') return
  if (queuedOrder.state !== 'pending' && queuedOrder.state !== 'queued') return
  if (queuedOrder.with_time_slot) return

  const action = queuedOrder.prioritized ? 'unprioritize' : 'prioritize'

  return {
    key: action,
    href: `queued_orders/${queuedOrder.id}/${action}`,
    label: t(action, { scope }),
    method: 'put'
  }
}

const cancelItem = (queuedOrder: OrderRow): ActionItem | undefined => {
  if (queuedOrder.record_type !== 'queued_order') return
  if (queuedOrder.state !== 'pending' && queuedOrder.state !== 'queued') return

  return {
    key: 'cancel',
    href: `queued_orders/${queuedOrder.id}/cancel`,
    label: t('cancel_order', { scope }),
    danger: true,
    method: 'put'
  }
}

// Cancelling stops at :queued, so once a meal is being cooked - or is already waiting in an
// unopened locker - disposal is the only way to keep it away from the customer.
const markForDisposalItem = (queuedOrder: OrderRow): ActionItem | undefined => {
  if (!queuedOrder.manual_disposal_requestable) return

  return {
    key: 'mark_for_disposal',
    href: `/queued_orders/${queuedOrder.id}/mark_for_disposal`,
    label: t('mark_for_disposal', { scope }),
    danger: true,
    method: 'put',
    confirm: t('confirm_mark_for_disposal', { scope })
  }
}

const actionItems = (queuedOrder: OrderRow): ActionItem[] =>
  [
    prioritizeItem(queuedOrder),
    cancelItem(queuedOrder),
    markForDisposalItem(queuedOrder),
    openLockerItem(queuedOrder)
  ].filter(Boolean) as ActionItem[]

const MealCell = ({ queuedOrder }: { queuedOrder: OrderRow }) => {
  const { id, recipe_name, record_type, asap, prioritized } = queuedOrder
  const isQueuedOrder = record_type === 'queued_order'

  return (
    <td className="meal-cell">
      <div className="meal-cell__id-row">
        {isQueuedOrder ? (
          <Tooltip title={t('see_details', { scope })}>
            <a
              href={`/queued_orders/${id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="meal-cell__id meal-cell__id--link"
            >
              <span>{`#${id}`}</span>
              <i className="fa fa-external-link meal-cell__details-icon" />
            </a>
          </Tooltip>
        ) : (
          <span className="meal-cell__id">{`#${id}`}</span>
        )}
        {asap && (
          <span className="meal-cell__badge meal-cell__badge--asap">
            {t('asap', { scope })}
          </span>
        )}
        {prioritized && (
          <span className="meal-cell__badge meal-cell__badge--priority">
            {t('priority', { scope })}
          </span>
        )}
      </div>
      <div className="meal-cell__name">{recipe_name || EMPTY_PLACEHOLDER}</div>
    </td>
  )
}

const LockerCell = ({ queuedOrder }: { queuedOrder: OrderRow }) => {
  const { locker_number, seconds_in_locker, in_locker } = queuedOrder
  // Still counting up vs. a settled figure - the two look identical, so they are
  // labelled and coloured apart. Touch delays make a plain tap open the tooltip and
  // hold it long enough to read, instead of MUI's default 700ms long press.
  const timeInLocker = seconds_in_locker != null && (
    <Tooltip
      title={t(in_locker ? 'in_locker' : 'time_in_locker', { scope })}
      enterTouchDelay={0}
      leaveTouchDelay={TOOLTIP_TOUCH_LINGER_MS}
    >
      <span
        tabIndex={0}
        className={`number-chip__caption${in_locker ? ' number-chip__caption--ongoing' : ''}`}
      >
        {formatDurationHms(seconds_in_locker)}
      </span>
    </Tooltip>
  )

  return (
    <td className="chip-cell">
      <NumberChip
        value={locker_number ? `#${locker_number}` : null}
        caption={timeInLocker}
      />
    </td>
  )
}

const QueuedOrderRow = ({
  tab,
  queuedOrderOrCommand
}: {
  tab: OrdersTab
  queuedOrderOrCommand: OrderRow
}) => {
  const {
    meal_status,
    state,
    created_at,
    planned_start_time,
    planned_end_time,
    queue_number,
    measured_meal_weight_display,
    voucher
  } = queuedOrderOrCommand

  return (
    <tr>
      <MealCell queuedOrder={queuedOrderOrCommand} />
      <LockerCell queuedOrder={queuedOrderOrCommand} />
      <td className="chip-cell">
        <NumberChip value={queue_number ? `#${queue_number}` : null} />
      </td>
      <TableCell value={measured_meal_weight_display} />
      <td className="status-cell">
        <div className="status-cell__badges">
          <StatusBadge status={meal_status || state} />
          <VoucherBadge voucher={voucher} />
        </div>
      </td>
      <TableCell value={created_at} />
      <TableCell value={planned_start_time} />
      <TableCell value={planned_end_time} />
      {tab === 'order_queue' && (
        <td className="actions-cell">
          <div className="actions-cell__buttons">
            <ActionsMenu items={actionItems(queuedOrderOrCommand)} />
          </div>
        </td>
      )}
    </tr>
  )
}

export default QueuedOrderRow
